// NOTE(IL, 2024-03-18): consider breaking this slice up further once it's working.
// I didn't want to isolate prematurely, but it might grow enough to be worth breaking apart.
import { logger } from '@automata/utils';
import {
  ExecutorOptionsInput,
  LabwareItemInput,
  LabwareTypeInput,
  ParameterDefinition,
  PlannerOptionsInput,
  TransportConfigInput,
  WorkflowInfo,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import omit from 'lodash/omit';

import { initialState } from './initialState';
import {
  DriverActionConfigsByActionNameByVersionedDriverName,
  WorkcellInstrumentsById,
  WorkflowState,
} from './types';
import { unbindParameters } from './utils';
import { SelectedWorkcellId } from '../../hooks/useGetWorkcell/types';
import { loadExistingWorkflow } from '../../migration';
import {
  AllTaskProperties,
  CodeTaskProperties,
  ConditionalTaskProperties,
  ContinuousLoadingProperties,
  DataExportTaskProperties,
  LabwareProperties,
  PropertiesType,
  TaskParameterValue,
  TaskProperties,
} from '../../types';
import {
  LabwareOffsetDefinition,
  labwareOffsetDefinitionSchema,
} from '../../zodSchemas/labwareOffsetDefinition';
import {
  createLabware,
  createNodesAndEdges,
  createTask,
  cutNodeToClipboard,
  deleteNodesEdges,
  loadScripts,
} from '../actions';
import { NULL_LABWARE_TYPE } from '../workflowJsonSelectors/constants';

export const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setComment: (
      state,
      action: PayloadAction<{ id: string; comment: string }>
    ) => {
      const { id, comment } = action.payload;
      state.visualWorkflow.comments[id] = comment;
    },
    setWorkflowDescription: (state, action: PayloadAction<string>) => {
      state.visualWorkflow.workflowDescription = action.payload;
    },
    setSchedulerVersion: (state, action: PayloadAction<string>) => {
      state.schedulerVersion = action.payload;
    },
    setWorkflowInstruments: (
      state,
      action: PayloadAction<WorkcellInstrumentsById>
    ) => {
      state.workcellInstruments = action.payload;
    },
    setWorkcellTransportConfig: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.workcellTransportConfigId = action.payload;
    },
    setWorkcellTransportConfigType: (
      state,
      action: PayloadAction<TransportConfigInput['type'] | undefined>
    ) => {
      state.workcellTransportConfigType = action.payload;
    },
    setWorkcellTransportConfigVersion: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.workcellTransportConfigVersion = action.payload;
    },
    setDriverActionConfigs: (
      state,
      action: PayloadAction<DriverActionConfigsByActionNameByVersionedDriverName>
    ) => {
      state.driverActionConfigs = action.payload;
    },
    setWorkflowName: (state, action: PayloadAction<string>) => {
      state.visualWorkflow.workflowName = action.payload;
    },
    setWorkflowAuthor: (state, action: PayloadAction<string>) => {
      state.visualWorkflow.workflowAuthor = action.payload;
    },
    setWorkflowMetadata: (state, action: PayloadAction<WorkflowInfo>) => {
      if (state.metadata) {
        state.metadata = {
          createdAt: action.payload.created_at,
          updatedAt: action.payload.updated_at,
          publishedAt: action.payload.published_at,
          createdBy: action.payload.creator_username,
          validBatchData: {
            reason: action.payload.valid_batch_data.reason,
            validBatchNumbers:
              action.payload.valid_batch_data.valid_batch_numbers,
          },
          syncedPlanId: action.payload.synced_plan_id,
        };
      }
    },
    setSelectedWorkcellId: (
      state,
      action: PayloadAction<SelectedWorkcellId>
    ) => {
      state.selectedWorkcellId = action.payload;
    },
    updateLabwareOffsetDefinitions: (
      state,
      action: PayloadAction<
        Partial<LabwareOffsetDefinition> & { labwareDefinitionURI: string }
      >
    ) => {
      const { labwareDefinitionURI, ...updated } = action.payload;
      const prev = state.labwareOffsetDefinitions[labwareDefinitionURI];
      const update = labwareOffsetDefinitionSchema.partial().safeParse(updated);

      if (!update.success) {
        logger.warn('Invalid offset definition update', update.error);
        return;
      }

      state.labwareOffsetDefinitions[labwareDefinitionURI] = {
        ...(prev ?? {}),
        ...updated,
      };
    },
    removeLabwareOffsetDefinition: (
      state,
      action: PayloadAction<{ labwareDefinitionURI: string }>
    ) => {
      const { labwareDefinitionURI } = action.payload;
      if (state.labwareOffsetDefinitions[labwareDefinitionURI] === undefined) {
        logger.warn(
          'tried to remove labwareOffsetDefinitions with nonexistent URI',
          labwareDefinitionURI
        );
        return;
      }
      delete state.labwareOffsetDefinitions[labwareDefinitionURI];
    },
    updatePlannerOptions: (
      state,
      action: PayloadAction<Partial<PlannerOptionsInput>>
    ) => {
      state.options.planner = {
        ...(state.options.planner ?? {}),
        ...action.payload,
      };
    },
    updateExecutorOptions: (
      state,
      action: PayloadAction<ExecutorOptionsInput>
    ) => {
      state.options.executor = {
        ...(state.options.executor ?? {}),
        ...action.payload,
      };
    },
    setShowAdvancedPlannerOptions: (state, action: PayloadAction<boolean>) => {
      state.showAdvancedPlannerOptions = action.payload;
    },
    updateLastPlanningTime: (state, action: PayloadAction<number | null>) => {
      state.lastPlanningTime = action.payload;
    },
    updateLabwareProperties: (
      state,
      action: PayloadAction<Partial<LabwareProperties> & { id: string }>
    ) => {
      const { id, ...updated } = action.payload;
      const prev = state.visualWorkflow.labwareProperties[id];

      if (prev === undefined) {
        logger.warn(
          'tried to update labwareProperties with nonexistent ID',
          id
        );
        return;
      }

      state.visualWorkflow.labwareProperties[id] = {
        ...prev,
        ...updated,
      };

      if ('totalUnits' in updated && updated.totalUnits === null) {
        Object.values(state.visualWorkflow.taskProperties).forEach((task) => {
          if (task.propertiesType === 'action') {
            if (id in task.labwareIdsToUnitsConsumed) {
              task.labwareIdsToUnitsConsumed[id] = null;
            }
          }
        });
      }
    },
    updateTaskProperties: (
      state,
      action: PayloadAction<
        (
          | Partial<TaskProperties>
          | Partial<CodeTaskProperties>
          | Partial<DataExportTaskProperties>
          | Partial<ConditionalTaskProperties>
          | Partial<ContinuousLoadingProperties>
        ) & { id: string }
      >
    ) => {
      const { id, ...updated } = action.payload;
      const prev = state.visualWorkflow.taskProperties[id];

      if (prev === undefined) {
        logger.warn('tried to update taskProperties with nonexistent ID', id);
        return;
      }

      if (
        updated.propertiesType === PropertiesType.Code &&
        prev.propertiesType === PropertiesType.Code
      ) {
        state.visualWorkflow.taskProperties[id] = {
          ...prev,
          ...updated,
        };
      } else if (
        updated.propertiesType === PropertiesType.DataExport &&
        prev.propertiesType === PropertiesType.DataExport
      ) {
        state.visualWorkflow.taskProperties[id] = {
          ...prev,
          ...updated,
        };
      } else if (
        updated.propertiesType === PropertiesType.Action &&
        prev.propertiesType === PropertiesType.Action
      ) {
        state.visualWorkflow.taskProperties[id] = {
          ...prev,
          ...updated,
        };
      } else if (
        updated.propertiesType === PropertiesType.Conditional &&
        prev.propertiesType === PropertiesType.Conditional
      ) {
        state.visualWorkflow.taskProperties[id] = {
          ...prev,
          ...updated,
        };
      } else if (
        updated.propertiesType === PropertiesType.ContinuousLoading &&
        prev.propertiesType === PropertiesType.ContinuousLoading
      ) {
        state.visualWorkflow.taskProperties[id] = {
          ...prev,
          ...updated,
        };
      } else {
        logger.error(
          `Tried to change propertiesType of task ${id}, this is not allowed.`
        );
      }
    },
    updateTaskParameters: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Record<string, TaskParameterValue>;
        isDataOutput: boolean; // it is used in graphSlice.ts (480 line)
      }>
    ) => {
      const { id, updates } = action.payload;
      const task = state.visualWorkflow.taskProperties[id];
      if (task === undefined) {
        logger.error(
          `tried to update task parameters with nonexistent task ID "${id}"`
        );
        return;
      }
      if (
        task.propertiesType === PropertiesType.Conditional ||
        task.propertiesType === PropertiesType.ContinuousLoading
      ) {
        logger.error(`tried to update task that doesn't have task parameters`);
        return;
      }
      const prev = task.taskParameters;
      task.taskParameters = { ...prev, ...updates };
    },
    removeTaskParameter: (
      state,
      action: PayloadAction<{
        id: string;
        fieldName: string;
      }>
    ) => {
      const { id, fieldName } = action.payload;
      const task = state.visualWorkflow.taskProperties[id];

      if (task === undefined) {
        logger.error(
          `tried to remove task parameter with nonexistent task ID "${id}"`
        );
        return;
      }

      if (
        task.propertiesType === PropertiesType.Conditional ||
        task.propertiesType === PropertiesType.ContinuousLoading
      ) {
        logger.error(
          `tried to remove task parameter from task that doesn't have task parameters`
        );
        return;
      }
      const prev = task.taskParameters;

      task.taskParameters = { ...prev };
      delete task.taskParameters[fieldName];
    },
    updateTaskAppliedParameters: (
      state,
      action: PayloadAction<{
        taskId: string;
        parameterName: string;
        parameterId: string;
      }>
    ) => {
      const { taskId, parameterName, parameterId } = action.payload;
      const task = state.visualWorkflow.taskProperties[taskId];

      if (!task) {
        logger.error(
          `tried to update task applied parameters with nonexistent task ID "${taskId}"`
        );
        return;
      }

      if (
        task.propertiesType === PropertiesType.Conditional ||
        task.propertiesType === PropertiesType.ContinuousLoading
      ) {
        logger.error(
          `tried to update applied parameters of task that doesn't have task parameters`
        );
        return;
      }

      const prev = task.taskAppliedParameters ?? {};
      task.taskAppliedParameters = { ...prev, [parameterName]: parameterId };
    },
    updateConditionAppliedParameter: (
      state,
      action: PayloadAction<{
        taskId: string;
        parameterName: string;
        parameterId: string;
      }>
    ) => {
      const { taskId, parameterName, parameterId } = action.payload;
      const task = state.visualWorkflow.taskProperties[taskId];

      if (!task) {
        logger.error(
          `tried to update condition parameter with nonexistent task ID "${taskId}"`
        );
        return;
      }

      if (task.propertiesType !== 'condition') {
        logger.error(
          `tried to update condition parameter on non-condition task ${taskId}`
        );
        return;
      }

      if ('comparison' in task && parameterName === 'sourceValue') {
        task.comparison = { ...task.comparison, lhs: { key: parameterId } };
      }
    },
    updateUnitsConsumed: (
      state,
      action: PayloadAction<{
        taskId: string;
        labwareId: string;
        unitsConsumed: number | null;
      }>
    ) => {
      const { taskId, labwareId, unitsConsumed } = action.payload;
      const task = state.visualWorkflow.taskProperties[taskId];
      if (task === undefined) {
        logger.error(
          `tried to update units consumed with nonexistent task ID "${taskId}"`
        );
        return;
      }
      if (task.propertiesType === 'action') {
        task.labwareIdsToUnitsConsumed[labwareId] = unitsConsumed;
      } else {
        logger.error(
          `tried to update units consumed of non-action task "${taskId}"`
        );
      }
    },
    setNumberOfBatches: (
      state,
      action: PayloadAction<{
        batches: number;
      }>
    ) => {
      state.visualWorkflow.batches = action.payload.batches;
    },
    addParameterDefinition: (
      state,
      action: PayloadAction<ParameterDefinition>
    ) => {
      const { id, name } = action.payload;
      const { parameterDefinitions } = state.visualWorkflow;
      const isDuplicateName = parameterDefinitions.find((p) => p.name === name);
      const isParameterAlreadyExists = parameterDefinitions.find(
        (p) => p.id === id
      );

      if (!id) {
        logger.error(
          `tried to create parameter definition that doesn't have id`
        );
        return;
      }
      if (isParameterAlreadyExists) {
        logger.error(`parameter definition with id "${id}" already exists"`);
        return;
      }
      if (!name) {
        logger.error(
          `tried to create parameter definition that doesn't have name`
        );
        return;
      }
      if (isDuplicateName) {
        logger.error(`parameter definition with name ${name} already exists`);
        return;
      }

      state.visualWorkflow.parameterDefinitions = [
        ...parameterDefinitions,
        action.payload,
      ];
    },
    updateParameterDefinition: (
      state,
      action: PayloadAction<ParameterDefinition>
    ) => {
      const { id, name } = action.payload;
      const { parameterDefinitions } = state.visualWorkflow;
      const index = parameterDefinitions.findIndex((p) => p.id === id);
      const isDuplicateName = parameterDefinitions.find(
        (p) => p.id !== id && p.name === name
      );

      if (index === -1) {
        logger.error(
          `tried to update parameter definition with nonexistent parameter ID "${id}"`
        );
        return;
      }
      if (!name) {
        logger.error(
          `tried to update parameter definition that doesn't have name`
        );
        return;
      }
      if (isDuplicateName) {
        logger.error(`parameter definition with name ${name} already exists`);
        return;
      }

      const prev = parameterDefinitions[index];
      parameterDefinitions[index] = { ...prev, ...action.payload };
    },
    removeParameterDefinition: (
      state,
      action: PayloadAction<{
        id: string;
      }>
    ) => {
      const { id } = action.payload;
      const { parameterDefinitions } = state.visualWorkflow;
      const parameter = parameterDefinitions.find((p) => p.id === id);

      if (parameter === undefined) {
        logger.error(
          `tried to remove parameter definition with nonexistent parameter ID "${id}"`
        );
        return;
      }
      // Remove the parameter from the definitions
      state.visualWorkflow.parameterDefinitions = parameterDefinitions.filter(
        (p) => p.id !== id
      );
      // Remove the parameter from task properties
      state.visualWorkflow.taskProperties = unbindParameters(
        state.visualWorkflow.taskProperties,
        [parameter],
        parameterDefinitions
      );
    },
    setParameterDefinitions: (
      state,
      action: PayloadAction<ParameterDefinition[]>
    ) => {
      state.visualWorkflow.parameterDefinitions = action.payload;
    },
    setParameterValues: (
      state,
      action: PayloadAction<ParameterDefinition[]>
    ) => {
      state.visualWorkflow.parameterValues = action.payload;
    },
    unbindParameterDefinitions: (
      state,
      action: PayloadAction<{
        parameters: ParameterDefinition[];
        checkIfDefaultChanged?: boolean;
      }>
    ) => {
      const { parameters, checkIfDefaultChanged = false } = action.payload;
      const { taskProperties, parameterDefinitions } = state.visualWorkflow;

      state.visualWorkflow.taskProperties = unbindParameters(
        taskProperties,
        parameters,
        parameterDefinitions,
        checkIfDefaultChanged
      );
    },
    unbindParameterDefinitionOfTask: (
      state,
      action: PayloadAction<{
        parameter?: ParameterDefinition;
        fieldName: string;
        taskId: string;
      }>
    ) => {
      const { parameter, fieldName, taskId } = action.payload;
      const { taskProperties, parameterDefinitions } = state.visualWorkflow;

      const task = taskProperties[taskId];

      if (!task) return logger.error(`Task with ID ${taskId} does not exist.`);
      if (task.propertiesType === PropertiesType.ContinuousLoading) {
        logger.error(
          "tried to unbind parameter from task that doesn't have task parameters"
        );
        return;
      }

      task.taskAppliedParameters ??= {};

      delete task.taskAppliedParameters[fieldName];

      if (!parameter) return;

      if (task.propertiesType === PropertiesType.Conditional) {
        // these tasks don't have task parameters, so we should ignore them
        logger.error(
          "tried to unbind parameter from task that doesn't have task parameters"
        );
        return;
      }

      task.taskParameters ??= {};

      // Find the parameter definition to get its default value
      const paramDef = parameterDefinitions.find((p) => p.id === parameter.id);

      task.taskParameters[fieldName] = paramDef?.default as TaskParameterValue;
    },
    unbindConditionParameter: (
      state,
      action: PayloadAction<{
        taskId: string;
      }>
    ) => {
      const { taskId } = action.payload;
      const { taskProperties } = state.visualWorkflow;

      const task = taskProperties[taskId];

      if (!task) return logger.error(`Task with ID ${taskId} does not exist.`);

      if (task.propertiesType !== PropertiesType.Conditional) {
        logger.error(
          `tried to unbind condition parameter from non-condition task ${taskId}`
        );
        return;
      }

      if ('comparison' in task && task.comparison.lhs) {
        task.comparison = {
          ...task.comparison,
          lhs: {
            ...task.comparison.lhs,
            key: '',
          },
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createLabware, (state, action) => {
      _createLabwareHelper(state, action.payload);
    });

    builder.addCase(deleteNodesEdges, (state, action) => {
      const labwareIds = action.payload.labwareIds;
      const taskIds = action.payload.taskIds;
      _removeNodePropertiesAndComments(state, { labwareIds, taskIds });
    });

    builder.addCase(cutNodeToClipboard, (state, action) => {
      switch (action.payload.dataType) {
        case 'labware':
          _removeNodePropertiesAndComments(state, {
            labwareIds: [action.payload.data.properties.id],
          });
          break;
        case 'task':
          _removeNodePropertiesAndComments(state, {
            taskIds: [action.payload.data.properties.id],
          });
          break;
        case 'multi':
          _removeNodePropertiesAndComments(state, {
            labwareIds: action.payload.labwareNodes.map(
              (node) => node.properties.id
            ),
            taskIds: action.payload.taskNodes.map((node) => node.properties.id),
          });
          break;
      }
    });

    builder.addCase(createTask, (state, action) => {
      _createTaskHelper(state, action.payload);
    });

    builder.addCase(createNodesAndEdges, (state, action) => {
      const { taskNodes, labwareNodes } = action.payload;

      for (const labware of labwareNodes) {
        _createLabwareHelper(state, labware);
      }

      for (const task of taskNodes) {
        _createTaskHelper(state, task);
      }
    });
    builder.addCase(loadExistingWorkflow, (state, action) => {
      // NOTE(IL, 2024-09-10): the "static" data (drivers, workcellInstruments)
      // should probably be split out into another slice(s) later

      // Determine if advanced planner options should be shown based on loaded config
      const plannerOptions = action.payload.plannerOptions;
      const hasAdvancedOptions = Boolean(
        plannerOptions &&
          (plannerOptions.target_objective !== null ||
            plannerOptions.max_dead_time !== null ||
            plannerOptions.preset !== null ||
            plannerOptions.round_robin_load_balancing !== false ||
            (plannerOptions.feasibility_stage !== undefined &&
              plannerOptions.feasibility_stage !== null) ||
            (plannerOptions.optimisation_stages !== undefined &&
              plannerOptions.optimisation_stages !== null &&
              plannerOptions.optimisation_stages.length > 0))
      );

      return {
        visualWorkflow: action.payload.visualBuilderSaved.visualWorkflow,
        metadata: action.payload.metadata,

        // "derived" data from the Workflow Config that is read-only and not saved back
        derived: action.payload.derived,

        // "static" data that doesn't get loaded from a Workflow Config
        scripts: state.scripts,
        drivers: state.drivers,
        labwareOffsetDefinitions: _parseLabwareOffsetDefinitionHelper(
          action.payload.visualBuilderSaved.labware_types,
          action.payload.visualBuilderSaved.labware
        ),
        driverActionConfigs: state.driverActionConfigs,
        workcellInstruments:
          action.payload.visualBuilderSaved.workcellInstruments,
        selectedWorkcellId:
          action.payload.visualBuilderSaved.selectedWorkcellId,
        workcellTransportConfigId:
          action.payload.visualBuilderSaved.workcellTransportConfigId ??
          state.workcellTransportConfigId,
        workcellTransportConfigType:
          action.payload.visualBuilderSaved.workcellTransportConfigType ??
          state.workcellTransportConfigType,
        workcellTransportConfigVersion:
          action.payload.visualBuilderSaved.workcellTransportConfigVersion ??
          state.workcellTransportConfigVersion,
        options: { ...state.options, planner: action.payload.plannerOptions },
        showAdvancedPlannerOptions: hasAdvancedOptions,
        lastPlanningTime: state.lastPlanningTime,
        schedulerVersion:
          action.payload.schedulerVersion || state.schedulerVersion,
      };
    });

    builder.addCase(loadScripts, (state, action) => {
      state.scripts = action.payload;
    });
  },
});

export function _createLabwareHelper(
  state: WorkflowState,
  args: { properties: LabwareProperties; comments?: string }
): void {
  const { comments, properties } = args;
  const { id } = properties;
  state.visualWorkflow.labwareProperties[id] = properties;
  if (comments !== undefined) {
    state.visualWorkflow.comments[id] = comments;
  }
}
function _parseLabwareOffsetDefinitionHelper(
  labwareTypes: LabwareTypeInput[],
  labwares: LabwareItemInput[]
): Record<string, LabwareOffsetDefinition> {
  return labwareTypes.reduce<Record<string, LabwareOffsetDefinition>>(
    (acc, item) => {
      const {
        id,
        gripping_widths,
        gripping_depth_offset,
        gripping_height_offset,
      } = item;
      const labwareType =
        labwares.find((l) => l.type === id)?.type ?? NULL_LABWARE_TYPE;

      const hasAnyOffset =
        gripping_widths != null ||
        gripping_depth_offset != null ||
        gripping_height_offset != null;

      acc[labwareType] = hasAnyOffset
        ? {
            gripping_widths,
            gripping_depth_offset,
            gripping_height_offset,
          }
        : null;

      return acc;
    },
    {}
  );
}

export function _createTaskHelper(
  state: WorkflowState,
  args: {
    properties: AllTaskProperties;
    comments?: string;
  }
): void {
  const { comments, properties } = args;
  const { id } = properties;

  if (
    properties.propertiesType !== 'condition' &&
    properties.propertiesType !== 'continuousLoading'
  ) {
    properties.labwareIdsToInputSlots = properties.labwareIdsToInputSlots ?? {};
    properties.labwareIdsToOutputSlots =
      properties.labwareIdsToOutputSlots ?? {};
  }

  state.visualWorkflow.taskProperties[id] = properties;

  if (comments !== undefined) {
    state.visualWorkflow.comments[id] = comments;
  }
}

export function _removeNodePropertiesAndComments(
  state: WorkflowState,
  args: { labwareIds?: string[]; taskIds?: string[] }
) {
  const { labwareIds = [], taskIds = [] } = args;

  state.visualWorkflow.labwareProperties = omit(
    state.visualWorkflow.labwareProperties,
    labwareIds
  );
  state.visualWorkflow.taskProperties = omit(
    state.visualWorkflow.taskProperties,
    taskIds
  );

  state.visualWorkflow.comments = omit(
    state.visualWorkflow.comments,
    ...[taskIds, labwareIds]
  );
}

export const {
  setComment,
  setWorkflowDescription,
  setSchedulerVersion,
  setWorkflowInstruments,
  setDriverActionConfigs,
  setWorkflowName,
  setWorkflowAuthor,
  updatePlannerOptions,
  updateExecutorOptions,
  setShowAdvancedPlannerOptions,
  updateLastPlanningTime,
  updateLabwareOffsetDefinitions,
  removeLabwareOffsetDefinition,
  updateLabwareProperties,
  updateTaskProperties,
  updateTaskParameters,
  removeTaskParameter,
  updateTaskAppliedParameters,
  updateConditionAppliedParameter,
  updateUnitsConsumed,
  setWorkflowMetadata,
  setWorkcellTransportConfig,
  setWorkcellTransportConfigType,
  setWorkcellTransportConfigVersion,
  setSelectedWorkcellId,
  setNumberOfBatches,
  addParameterDefinition,
  updateParameterDefinition,
  removeParameterDefinition,
  setParameterDefinitions,
  setParameterValues,
  unbindParameterDefinitions,
  unbindParameterDefinitionOfTask,
  unbindConditionParameter,
} = workflowSlice.actions;

export default workflowSlice.reducer;
